import type {
  AbstractPartDefinition,
  Blueprint,
  BlueprintRenderer,
  DragConstraint,
  DragUpdateResult,
  PartRenderer,
  SnapAnchor,
} from "@project/emerge-types";
import { createElement } from "react";
import { Vector3 } from "three";
import { DraggablePart } from "../components/DraggablePart";
import { useCameraControls } from "./ProductViewerCamera";

export const BlueprintRendererComponent = ({
  blueprint,
  renderer,
  isHidden,
}: {
  blueprint: Blueprint;
  renderer: BlueprintRenderer | undefined;
  isHidden: boolean;
}) => {
  if (isHidden || !renderer) return null;
  return createElement(renderer, { blueprint, key: "blueprint" });
};

export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

/** Placement position: for PianoHinge use positions[0], otherwise part.position. */
function getPartPlacementPosition(
  part: AbstractPartDefinition<string>,
): Vector3 {
  if (
    part.type === "PianoHinge" &&
    "positions" in part &&
    Array.isArray((part as { positions?: Vector3[] }).positions) &&
    (part as { positions: Vector3[] }).positions.length > 0
  ) {
    const positions = (part as { positions: Vector3[] }).positions;
    const p = positions[0];
    if (p) return new Vector3(p.x, p.y, p.z);
  }
  if ("position" in part && part.position) {
    return part.position;
  }
  return new Vector3(0, 0, 0);
}

export const PartRendererComponent = ({
  part,
  renderer,
  group,
  onDragUpdate,
}: {
  part: AbstractPartDefinition<string>;
  renderer: PartRenderer | undefined;
  group: string;
  onDragUpdate?: (
    partId: string,
    position: Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
  ) => DragDisplayValues | undefined;
}) => {
  const cameraControls = useCameraControls();
  const placementPosition = getPartPlacementPosition(part);

  if (!renderer) return null;

  const partContent = createElement(renderer, {
    id: part.id || "",
    key: `renderer-${part.id || part.type}-${group}`,
  });

  if (part.dragConstraint && onDragUpdate) {
    return (
      <group
        visible={!part.hidden}
        key={`part-${part.id || part.type}-${group}`}
      >
        <DraggablePart
          partId={part.id}
          position={placementPosition}
          rotation={part.rotation}
          constraint={part.dragConstraint}
          debug={false}
          controlled={true}
          onDragStart={() => {
            return cameraControls?.disableControls();
          }}
          onDrag={(newPosition: Vector3) => {
            if (part.dragConstraint && onDragUpdate) {
              return onDragUpdate(
                part.id,
                newPosition,
                part.dragConstraint,
                part.settingsContext,
              );
            }
          }}
          onDragEnd={(finalPos: Vector3, _snappedAnchor?: SnapAnchor) => {
            if (part.dragConstraint) {
              onDragUpdate(
                part.id,
                finalPos,
                part.dragConstraint,
                part.settingsContext,
              );
            }
          }}
        >
          {partContent}
        </DraggablePart>
      </group>
    );
  }

  return (
    <group
      position={[placementPosition.x, placementPosition.y, placementPosition.z]}
      rotation={[part.rotation.x, part.rotation.y, part.rotation.z]}
      visible={!part.hidden}
      key={`part-${part.id || part.type}-${group}`}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerEnter={(e) => e.stopPropagation()}
      onPointerLeave={(e) => e.stopPropagation()}
    >
      {partContent}
    </group>
  );
};
